import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { RiskWarningBanner } from "./components/layout/RiskWarningBanner";
import { useSubdomainRedirect } from "./hooks/useSubdomainRedirect";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import HomePage from "./pages/HomePage";
import CampaignComparePage from "./pages/CampaignComparePage";
import { comparisonCampaigns } from "./lib/comparisonCampaigns";
import BrokerPage from "./pages/BrokerPage";
import ComparePage from "./pages/ComparePage";
import GuidesPage from "./pages/GuidesPage";
import ArticlePage from "./pages/ArticlePage";
import LegalPage from "./pages/LegalPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import AdminPage from "./pages/AdminPage";
import LoginPage from "./pages/LoginPage";

import { DisclosureNotice } from "./components/layout/DisclosureNotice";

function Layout({ children, showRiskWarning = true }) {
  useSubdomainRedirect();
  const location = useLocation();

  // Re-run full MaxConv tracking script on every route change
  useEffect(() => {
    !function(w,d,o,n){
      w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
      var s=d.createElement(n),t=d.getElementsByTagName(n)[0];
      s.async=true;s.defer=true;
      s.src="//w7w5lh.mcgo2.com/t/7df7c524-8548-4e1f-a80a-2dee1a613ef5.js";
      t.parentNode.insertBefore(s,t);
    }(window,document,"maxconv","script");
  }, [location.pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {showRiskWarning && <RiskWarningBanner />}
      <main className="flex-1">{children}</main>
      <DisclosureNotice />
      <Footer />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />

            {/* Public routes with layout */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            {comparisonCampaigns.map(campaign => (
              <Route key={campaign.path} path={campaign.path} element={<Layout showRiskWarning={false}><CampaignComparePage campaign={campaign} /></Layout>} />
            ))}
            <Route path="/compare" element={<Layout><ComparePage /></Layout>} />
            <Route path="/guides" element={<Layout><GuidesPage /></Layout>} />
            <Route path="/guides/:slug" element={<Layout><ArticlePage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />

            {/* Legal pages */}
            <Route path="/privacy-policy" element={<Layout><LegalPage /></Layout>} />
            <Route path="/terms-and-conditions" element={<Layout><LegalPage /></Layout>} />
            <Route path="/affiliate-disclosure" element={<Layout><LegalPage /></Layout>} />
            <Route path="/disclaimer" element={<Layout><LegalPage /></Layout>} />

            {/* Broker pages - must be last (catch-all slug) */}
            <Route path="/:slug" element={<Layout><BrokerPage /></Layout>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
